import { supabase } from './supabase';
import type { SiteConfig } from '../types';

// Base URL for site paths - use relative paths
const SITE_BASE_URL = '/site';

// Save site configuration
export async function saveSiteConfig(config: SiteConfig) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { error } = await supabase
    .from('site_config')
    .upsert({
      company_id: user.id,
      config,
      updated_at: new Date().toISOString()
    });

  if (error) throw error;
}

// Load saved site configuration
export async function loadSiteConfig() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('site_config')
    .select('config')
    .eq('company_id', user.id)
    .maybeSingle();

  if (error) {
    console.error('Error loading site config:', error);
    return null;
  }

  return data?.config as SiteConfig;
}

// Generate preview URL
export function generatePreviewUrl(config: SiteConfig) {
  if (!config) return null;

  // Use URL-safe base64 encoding
  const safeConfig = btoa(JSON.stringify(config))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
  return `${SITE_BASE_URL}/preview/${safeConfig}`;
}

// Publish site and create deployment
export async function publishSite(config: SiteConfig) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // First save the config
  await saveSiteConfig(config);

  // Get unique URL path from database function
  const { data: urlPath, error: pathError } = await supabase
    .rpc('generate_unique_url_path', { company_name: config.header.title });
  
  if (pathError) throw pathError;

  // Create deployment record
  const { data: deployment, error: deployError } = await supabase
    .from('site_deployments')
    .upsert({
      company_id: user.id,
      url_path: urlPath,
      status: 'published',
      config,
      updated_at: new Date().toISOString()
    })
    .select()
    .single();

  if (deployError) throw deployError;

  // Return the URL with path
  const siteUrl = `${SITE_BASE_URL}/${btoa(JSON.stringify(config))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')}`;
  
  return {
    ...deployment,
    url: siteUrl
  };
}

// Get latest deployed site
export async function getDeployedSite() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('site_deployments')
    .select('*')
    .eq('company_id', user.id)
    .eq('status', 'published')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;

  if (data) {
    return {
      ...data,
      url: `${SITE_BASE_URL}/${btoa(JSON.stringify(data.config))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '')}`
    };
  }

  return null;
}